/**
 * Client-side helpers to present monthly inventory snapshot_data like grouped inventory API responses.
 */

function normalizeFilterArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter((v) => v != null && String(v).trim() !== "");
  if (val === "all") return [];
  return [val];
}

function matchesFilterValue(recordValue, allowed) {
  if (!allowed.length) return true;
  const rv = (recordValue ?? "").toString().trim().toLowerCase();
  if (!rv) return false;
  return allowed.some((a) => rv === String(a).trim().toLowerCase());
}

/**
 * Apply the same default status filter as useInventoryData when status is empty.
 * @param {Object} filters - Inventory filter state from FilterBar / useInventoryData
 * @param {Array<Object>} records
 * @returns {Array<Object>}
 */
export function filterSnapshotRecords(records, filters) {
  if (!Array.isArray(records)) return [];

  let providers = normalizeFilterArray(filters.provider);
  let categories = normalizeFilterArray(filters.category);
  let statuses = normalizeFilterArray(filters.status);
  if (statuses.length === 0) {
    statuses = ["In Service", "suspended"];
  }
  const serviceTypes = normalizeFilterArray(filters.serviceType);
  const billingAccounts = normalizeFilterArray(filters.billingAccount);
  const search = (filters.search || "").trim().toLowerCase();

  const siteRegion = normalizeFilterArray(filters.siteRegion);
  const siteClassification = normalizeFilterArray(filters.siteClassification);
  const siteGeneralManagerId = normalizeFilterArray(filters.siteGeneralManagerId);
  const siteSdWanAppliance = normalizeFilterArray(filters.siteSdWanAppliance);
  const siteStatus = normalizeFilterArray(filters.siteStatus);
  const circuitAction = normalizeFilterArray(filters.circuitAction);

  return records.filter((r) => {
    if (!r || typeof r !== "object") return false;

    if (providers.length && !matchesFilterValue(r.provider, providers)) return false;
    if (categories.length && !matchesFilterValue(r.category, categories)) return false;
    if (statuses.length && !matchesFilterValue(r.status, statuses)) return false;
    if (serviceTypes.length && !matchesFilterValue(r.service_type, serviceTypes)) return false;
    if (billingAccounts.length && !matchesFilterValue(r.billing_account, billingAccounts))
      return false;

    if (search) {
      try {
        const blob = JSON.stringify(r).toLowerCase();
        if (!blob.includes(search)) return false;
      } catch {
        return false;
      }
    }

    if (siteRegion.length && !matchesFilterValue(r.site_region, siteRegion)) return false;
    if (siteClassification.length && !matchesFilterValue(r.site_classification, siteClassification))
      return false;
    if (
      siteGeneralManagerId.length &&
      !matchesFilterValue(r.site_general_manager_id, siteGeneralManagerId)
    )
      return false;
    if (
      siteSdWanAppliance.length &&
      !matchesFilterValue(r.site_sd_wan_appliance, siteSdWanAppliance)
    )
      return false;
    if (siteStatus.length && !matchesFilterValue(r.site_status, siteStatus)) return false;
    if (circuitAction.length && !matchesFilterValue(r.action, circuitAction)) return false;

    return true;
  });
}

function hasNoAddress(group) {
  if (group.addressKey && String(group.addressKey).startsWith("no-address-")) return true;
  const hasAddress =
    (group.std_street_address && String(group.std_street_address).trim() !== "") ||
    (group.std_city && String(group.std_city).trim() !== "") ||
    (group.std_state && String(group.std_state).trim() !== "") ||
    (group.std_zip && String(group.std_zip).trim() !== "") ||
    (group.std_country && String(group.std_country).trim() !== "");
  return !hasAddress;
}

/**
 * Sort address groups (same idea as backend grouped inventory).
 * @param {Array<Object>} groups
 * @param {'asc'|'desc'} order
 */
export function sortSnapshotAddressGroups(groups, order = "asc") {
  const isDesc = order === "desc";
  const copy = [...groups];
  copy.sort((a, b) => {
    const aNo = hasNoAddress(a);
    const bNo = hasNoAddress(b);
    if (aNo !== bNo) {
      if (isDesc) return aNo ? -1 : 1;
      return aNo ? 1 : -1;
    }
    if (aNo && bNo) {
      const aId = a.items?.[0]?.id ?? 0;
      const bId = b.items?.[0]?.id ?? 0;
      return aId - bId;
    }
    const streetA = (a.std_street_address || "").trim().toLowerCase();
    const streetB = (b.std_street_address || "").trim().toLowerCase();
    let cmp = streetA.localeCompare(streetB);
    if (cmp !== 0) return isDesc ? -cmp : cmp;
    const cityA = (a.std_city || "").trim().toLowerCase();
    const cityB = (b.std_city || "").trim().toLowerCase();
    cmp = cityA.localeCompare(cityB);
    if (cmp !== 0) return isDesc ? -cmp : cmp;
    const stateA = (a.std_state || "").trim().toLowerCase();
    const stateB = (b.std_state || "").trim().toLowerCase();
    cmp = stateA.localeCompare(stateB);
    if (cmp !== 0) return isDesc ? -cmp : cmp;
    const zipA = (a.std_zip || "").trim().toLowerCase();
    const zipB = (b.std_zip || "").trim().toLowerCase();
    cmp = zipA.localeCompare(zipB);
    return isDesc ? -cmp : cmp;
  });
  return copy;
}

/**
 * Group flat snapshot records by standardized address key (aligned with inventory import grouped API).
 * @param {Array<Object>} records
 * @returns {Array<{ addressKey, std_*, items, totalMRC, totalCount, site_status }>}
 */
export function groupSnapshotRecordsByAddress(records) {
  if (!Array.isArray(records) || records.length === 0) return [];

  const groupsMap = new Map();
  let noAddressSeq = 0;

  for (const record of records) {
    if (!record || typeof record !== "object") continue;

    const street = record.std_street_address ?? record.street_address ?? "";
    const city = record.std_city ?? record.city ?? "";
    const state = record.std_state ?? record.state ?? "";
    const zip = record.std_zip ?? record.zip ?? "";
    const country = record.std_country ?? record.country ?? "";

    const addressKey = [street, city, state]
      .map((s) => String(s || "").trim())
      .join("|");
    const key =
      addressKey === "||" || addressKey === ""
        ? `no-address-${record.id != null ? record.id : noAddressSeq++}`
        : addressKey;

    if (!groupsMap.has(key)) {
      groupsMap.set(key, {
        addressKey: key,
        std_street_address: record.std_street_address ?? record.street_address ?? "",
        std_city: record.std_city ?? record.city ?? "",
        std_state: record.std_state ?? record.state ?? "",
        std_zip: record.std_zip ?? record.zip ?? "",
        std_country: record.std_country ?? record.country ?? "",
        site_status: record.site_status ?? null,
        site_name: record.site_name ?? "",
        needs_master: Boolean(record.needs_master),
        items: [],
        totalMRC: 0,
        totalCount: 0,
      });
    }

    const group = groupsMap.get(key);
    group.items.push(record);
    const mrc = parseFloat(record.mrc) || 0;
    group.totalMRC += mrc;
    group.totalCount += 1;

    if (zip && !group.std_zip) group.std_zip = zip;
    if (country && !group.std_country) group.std_country = country;
  }

  return sortSnapshotAddressGroups(Array.from(groupsMap.values()), "asc");
}

export function sumSnapshotGroupsMRC(groups) {
  if (!Array.isArray(groups)) return 0;
  return groups.reduce((sum, g) => sum + (parseFloat(g.totalMRC) || 0), 0);
}
